import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CardLayoutComponent, CardSection } from '../shared/card-layout/card-layout.component';
import { Store } from '@ngrx/store';
import { FormattedValuePipe } from '../shared/pipes/formatted-value.pipe';

@Component({
    selector: 'app-payment-information',
    imports: [CardLayoutComponent],
    template: `
        <app-card-layout
            (click)="isActive = !isActive"
            [isActive]="isActive"
            title="Payment information"
            [isLoading]="isDCBillingLoaded"
            [failed]="dcBillingFailed"
            [sections]="sections"
            (retry)="retryDCBilling()"
        ></app-card-layout>
    `,
})
export class PaymentInformationComponent {
    isActive = false;
    @Input() isDCBillingLoaded = false;
    @Input() dcBillingFailed = false;
    @Output() retry = new EventEmitter<void>();
    private store = inject(Store);
    policySignal = this.store.selectSignal((state: any) => state.policy);
    private formattedValuePipe = new FormattedValuePipe();

    private formatCurrencyValue(value: any): string {
        if (value === null || value === undefined || value === '') return '$0.00';

        if (typeof value === 'number') {
            const abs = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return value < 0 ? `-$${abs}` : `$${abs}`;
        }

        if (typeof value === 'string') {
            const normalizedValue = value.replace(/[^0-9.-]/g, '');
            if (!normalizedValue) return '$0.00';

            const parsedValue = Number(normalizedValue);
            return Number.isFinite(parsedValue) ? `$${parsedValue.toFixed(2)}` : `$${value}`;
        }

        return '$0.00';
    }

    get sections(): CardSection[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        // const data = policyState.data?.[policyNum]?.billingSnapshot?.paymentInformation;
        const billingSnapshot = policyState.data?.[policyNum]?.billingSnapshot; // ← root level
        const data = billingSnapshot?.paymentInformation;

        if (!data) return [];

        return [
            {
                title: 'Current statement',
                icon: 'quote-icon',
                fields: [
                    {
                        caption:
                            'Due on ' + this.formattedValuePipe.transformDate(data?.currentStatement?.dueDate?.value),
                        valueHtml:
                            '<h5 style="display:inline;margin:0"><strong>' +
                            this.formatCurrencyValue(
                                data?.currentStatement?.amountToBePaidTodayInclusiveOfPastDue?.value
                            ) +
                            '</strong></h5>',
                        description: '<span class="disclaimer"> inclusive of past due</span>',
                    },
                    {
                        caption: 'Outstanding balance',
                        valueHtml: this.formatCurrencyValue(data?.currentStatement?.outstandingBalance?.value),
                    },
                    {
                        caption: 'Past due amount',
                        valueHtml: this.formatCurrencyValue(data?.currentStatement?.pastDueAmount?.value),
                    },

                    { caption: 'Invoice number', value: data?.currentStatement?.invoiceNumber?.value ?? '' },
                ],
            },
            {
                title: 'Payment frequency',
                icon: 'payment-frequency',
                collapsible: true,
                initialVisibleFields: 1,
                loadMoreLabel: 'Show more',
                columns: ['DUE DATE', 'RECEV.', 'TRANS.', 'INSTALL.BALANCE'],
                fields: [
                    ...(data?.installmentSchedule?.schedule ?? []).map((s: any, i: number) => ({
                        caption: this.formattedValuePipe.formatDateOnly(s?.items?.[0]?.dueDate?.value) ?? '',
                        value: this.formatCurrencyValue(s?.totalBalance?.value),
                        hasSeparator: true,
                        captionTag: i === 0 ? 'Next payment' : undefined,
                        children: (s?.items ?? []).map((item: any) => ({
                            caption: this.formattedValuePipe.formatDateOnly(item?.dueDate?.value) ?? '',
                            description: item?.receivableTypeCode?.value ?? '',
                            typeCode: item?.transactionTypeCode?.value ?? '',
                            premium: this.formatCurrencyValue(item?.balance?.value),
                        })),
                    })),
                ],
            },
            {
                title: 'Payment history',
                icon: 'payment-history',
                collapsible: true,
                initialVisibleFields: 3,
                loadMoreLabel: 'Show more',
                fields: (data?.paymentHistory ?? []).map((r: any) => ({
                    caption: this.formattedValuePipe.transformDate(r?.entryDate?.value) ?? '',
                    value: this.formatCurrencyValue(r?.amount?.value),
                    description: this.getPaymentDescription(r),
                    boldValue: true,
                })),
            },
            {
                title: 'Refund (Item in suspense)',
                icon: 'Refund-icon',
                fields: [
                    {
                        caption: 'Refund amount',
                        value: this.formatCurrencyValue(billingSnapshot?.paymentType?.refundAmount?.value), // ← was paymentDetails
                        boldValue: true,
                    },
                ],
            },
        ];
    }

    private getPaymentDescription(r: any): string {
        const method = r?.paymentMethod?.value ?? '';
        const cardType = r?.cardType?.value;
        const cardNumber = r?.cardNumber?.value;
        if (cardType && cardNumber) {
            return `${cardType} **** ${cardNumber}`;
        }
        return method;
    }

    retryDCBilling() {
        this.retry.emit();
    }
}
